import ApiService from "../../../services/ApiService";
import DatamuseService from "../../../services/DatamuseService";
import Logger from "../../../services/Logger";
import BannedService from "../../setting/banned_words/BannedService";
import AdminWordWheelBoardService, {
    DRAFT,
    PUBLISHED,
    READY_TO_REVIEW,
    REVIEWED,
} from "../board/AdminWordWheelBoardService";
import { buildPublishMetadata } from "../wordWheelLevelUtils";
import { parsePuzzleDetails } from "../wordWheelDisplayUtils";

const getWordListFromTemplate = (tpl) => {
    if (tpl?.filledCoordinates) {
        try {
            const parsed = JSON.parse(tpl.filledCoordinates);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed.map((item) => item.text).filter(Boolean);
            }
        } catch (error) {
            Logger.error("Failed to parse filledCoordinates:", error);
        }
    }
    return (tpl?.wordsInUse || "")
        .split("\n")
        .map((word) => word.trim())
        .filter(Boolean);
};

const parseWordsInUseLines = (wordsInUse) =>
    (wordsInUse || "")
        .split(/[\n,;]+/)
        .map((w) => w.trim())
        .filter(Boolean);

/** Word Wheel blocked banned categories (high_frequency is allowed on the wheel). */
const WORD_WHEEL_BLOCKED_BANNED_CATEGORIES = new Set([
    "profanity",
    "violence",
    "drugs",
    "offensive",
    "inappropriate",
    "other",
]);

/** Banned rows that must not appear in Word Wheel puzzles. */
const relevantBannedWordsForTemplate = (bannedList) =>
    (Array.isArray(bannedList) ? bannedList : []).filter((banned) => {
        if (!banned) return false;
        const category = String(banned.category || "").trim().toLowerCase();
        return WORD_WHEEL_BLOCKED_BANNED_CATEGORIES.has(category);
    });

const findBannedHitsWithReasons = (tpl, bannedList) => {
    const relevant = relevantBannedWordsForTemplate(bannedList);
    if (relevant.length === 0) return [];
    const bannedByKey = new Map();
    for (const banned of relevant) {
        const payload = {
            word: banned.word || banned.wordNorm,
            category: banned.category,
            isKids: Boolean(banned.isKids),
        };
        if (banned.word) {
            bannedByKey.set(String(banned.word).toLowerCase(), payload);
        }
        if (banned.wordNorm) {
            const key = String(banned.wordNorm).toLowerCase();
            if (!bannedByKey.has(key)) {
                bannedByKey.set(key, payload);
            }
        }
    }
    const hits = [];
    const seen = new Set();
    for (const word of parseWordsInUseLines(tpl?.wordsInUse)) {
        const key = word.toLowerCase();
        if (!bannedByKey.has(key) || seen.has(key)) continue;
        seen.add(key);
        hits.push(bannedByKey.get(key));
    }
    return hits;
};

const findBannedHitsInTemplate = (tpl, bannedList) =>
    findBannedHitsWithReasons(tpl, bannedList).map((hit) => hit.word);

const buildListParams = (paginationModel, sort, keyword, state, language, seasonTag, includeGrid = false) => {
    const param = {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
        keyword: keyword ? encodeURIComponent(keyword) : "",
    };
    if (state) param.state = state;
    if (language && language !== "all") param.language = language;
    if (seasonTag) param.season = seasonTag;
    if (includeGrid) param.includeGrid = true;
    return param;
};

const fetchAllTemplatesMatching = async (sort, keyword, state, language, seasonTag, includeGrid = false) => {
    const pageSize = 100;
    const allRows = [];
    let page = 0;
    let totalElements = null;

    while (totalElements == null || allRows.length < totalElements) {
        const param = buildListParams(
            { page, pageSize },
            sort,
            keyword,
            state,
            language,
            seasonTag,
            includeGrid,
        );
        const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl", param);
        const content = result?.content || [];
        totalElements = result?.page?.totalElements ?? allRows.length + content.length;
        allRows.push(...content);
        if (content.length === 0) break;
        page += 1;
        if (page > 200) break;
    }
    return allRows;
};

const loadBannedByLanguage = async (language) => {
    const langs =
        language && language !== "all" ? [language] : ["english", "french"];
    const entries = await Promise.all(
        langs.map(async (lang) => {
            try {
                const list = await BannedService.requestList(lang);
                return [lang, Array.isArray(list) ? list : []];
            } catch (err) {
                Logger.warn(`Failed to load banned words for ${lang}:`, err);
                return [lang, []];
            }
        }),
    );
    return Object.fromEntries(entries);
};

const promoteOneToReviewed = async (tpl, publishOptions = null) => {
    const id = tpl.id;
    let state = tpl.state;

    if (state === DRAFT) {
        const readyResult = await AdminWordWheelBoardService.updateTplState(id, READY_TO_REVIEW);
        if (readyResult?.code === "FAILURE") {
            throw new Error(readyResult.message || "Failed to move to ready to review");
        }
        state = READY_TO_REVIEW;
    }

    if (state === READY_TO_REVIEW) {
        const wordList = getWordListFromTemplate(tpl);
        const wordScores = await DatamuseService.requestWordScores(wordList);
        const details = publishOptions
            ? buildPublishMetadata(tpl, wordScores, publishOptions).details
            : wordScores;
        const reviewedResult = await AdminWordWheelBoardService.updateTplState(id, REVIEWED, details);
        if (reviewedResult?.code === "FAILURE") {
            throw new Error(reviewedResult.message || "Failed to mark as reviewed");
        }
        return reviewedResult;
    }

    if (state === REVIEWED || state === PUBLISHED) {
        return { code: "SUCCESS", skipped: true };
    }

    throw new Error(`Cannot mark as reviewed from state ${state}`);
};

const buildPublishItemForTemplate = async (tpl, publishOptions = null) => {
    const wordList = getWordListFromTemplate(tpl);
    const wordScores = await DatamuseService.requestWordScores(wordList);
    const metadata = publishOptions
        ? buildPublishMetadata(tpl, wordScores, publishOptions)
        : { details: wordScores };

    return {
        id: tpl.id,
        comment: publishOptions?.comment ?? tpl.comment ?? "",
        details: metadata.details,
        isFree: publishOptions?.isFree ?? tpl.free ?? false,
        tpl,
    };
};

const SEASON_PUBLISHED_PAGE_SIZE = 1000;

/** Safety net so a bad total from the API can never turn paging into an endless request loop. */
const SEASON_PUBLISHED_MAX_PAGES = 30;

/** Season rows are read by both the stats section and the season dialog; reuse a recent read. */
const SEASON_PUBLISHED_CACHE_TTL_MS = 15000;

const seasonPublishedCache = new Map();
const seasonPublishedInFlight = new Map();

const seasonPublishedKey = (season, language) => `${season ?? ""}|${language ?? ""}`;

const loadPublishedBySeason = async (season, language) => {
    const allRows = [];

    for (let page = 0; page < SEASON_PUBLISHED_MAX_PAGES; page += 1) {
        const param = {
            page,
            pageSize: SEASON_PUBLISHED_PAGE_SIZE,
            sort: "publishedAt:asc",
            state: PUBLISHED,
            season,
        };
        if (language && language !== "all") {
            param.language = language;
        }
        const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl", param);
        const content = Array.isArray(result?.content) ? result.content : [];
        allRows.push(...content);

        if (content.length < SEASON_PUBLISHED_PAGE_SIZE) {
            return allRows;
        }
        const totalPages = Number(result?.page?.totalPages);
        if (Number.isFinite(totalPages) && page + 1 >= totalPages) {
            return allRows;
        }
        const totalElements = Number(result?.page?.totalElements);
        if (Number.isFinite(totalElements) && allRows.length >= totalElements) {
            return allRows;
        }
    }

    Logger.warn(
        `Stopped reading published ${season} templates at ${SEASON_PUBLISHED_MAX_PAGES} pages (${allRows.length} rows).`,
    );
    return allRows;
};

const AdminWordWheelService = {
    requestList: async (
        paginationModel,
        sort,
        setRows,
        setRowCount,
        keyword,
        state,
        language,
        seasonTag = null,
        { onlyWithBannedWords = false, includeGrid = false } = {},
    ) => {
        if (!onlyWithBannedWords) {
            const param = buildListParams(paginationModel, sort, keyword, state, language, seasonTag, includeGrid);
            Logger.log(param);
            const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl", param);
            setRows(result.content || []);
            setRowCount(result.page?.totalElements ?? 0);
            return;
        }

        const [bannedByLanguage, allRows] = await Promise.all([
            loadBannedByLanguage(language),
            fetchAllTemplatesMatching(sort, keyword, state, language, seasonTag, includeGrid),
        ]);

        const matched = [];
        for (const tpl of allRows) {
            const lang = String(tpl?.language || "english").toLowerCase();
            const bannedList = bannedByLanguage[lang] || bannedByLanguage.english || [];
            const bannedHits = findBannedHitsInTemplate(tpl, bannedList);
            if (bannedHits.length === 0) continue;
            matched.push({ ...tpl, bannedHits });
        }

        const page = Math.max(0, Number(paginationModel?.page) || 0);
        const pageSize = Math.max(1, Number(paginationModel?.pageSize) || 15);
        const start = page * pageSize;
        setRows(matched.slice(start, start + pageSize));
        setRowCount(matched.length);
    },

    countPublishedInSeason: async (season, language = "all") => {
        const param = { season };
        if (language && language !== "all") {
            param.language = language;
        }
        const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl/published-count", param);
        return result?.count ?? 0;
    },

    /** Drops the cached season rows so the next read hits the API again. */
    clearPublishedBySeasonCache: () => {
        seasonPublishedCache.clear();
    },

    fetchPublishedBySeason: async (season, language = "english", { force = false } = {}) => {
        const key = seasonPublishedKey(season, language);

        if (!force) {
            const cached = seasonPublishedCache.get(key);
            if (cached && Date.now() - cached.at < SEASON_PUBLISHED_CACHE_TTL_MS) {
                return cached.rows;
            }
            const pending = seasonPublishedInFlight.get(key);
            if (pending) {
                return pending;
            }
        }

        const request = loadPublishedBySeason(season, language)
            .then((rows) => {
                seasonPublishedCache.set(key, { at: Date.now(), rows });
                return rows;
            })
            .finally(() => {
                seasonPublishedInFlight.delete(key);
            });
        seasonPublishedInFlight.set(key, request);
        return request;
    },

    fetchTemplatesByState: async (state, language = "all") => {
        const pageSize = 100;
        const allRows = [];
        let page = 0;
        let totalElements = null;

        while (totalElements == null || allRows.length < totalElements) {
            const param = {
                page,
                pageSize,
                sort: "createdAt:asc",
                state,
            };
            if (language && language !== "all") {
                param.language = language;
            }
            const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl", param);
            const content = result.content || [];
            allRows.push(...content);
            totalElements = result.page?.totalElements ?? allRows.length;
            if (content.length === 0) {
                break;
            }
            page += 1;
        }

        return allRows;
    },

    countTemplatesByState: async (state, language = "all") => {
        const param = {
            page: 0,
            pageSize: 1,
            sort: "createdAt:desc",
            state,
        };
        if (language && language !== "all") {
            param.language = language;
        }
        const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl", param);
        return result.page?.totalElements ?? 0;
    },

    assignSeasonLevels: async (seasonTag, language = "english") => {
        const result = await AdminWordWheelBoardService.assignSeasonLevels({
            season: seasonTag,
            language,
        });
        seasonPublishedCache.clear();
        return {
            updated: result?.updated ?? 0,
            levelLocked: result?.levelLocked ?? true,
        };
    },

    updateTemplateLevels: async (templates, puzzleLevel) => {
        const succeeded = [];
        const failed = [];

        for (const tpl of templates) {
            try {
                const existing = parsePuzzleDetails(tpl.details) || {};
                const details = {
                    ...existing,
                    puzzleLevel,
                    calculatedLevel: existing.calculatedLevel ?? puzzleLevel,
                };
                await AdminWordWheelBoardService.updateTemplateInfo(tpl.id, { details });
                succeeded.push(tpl);
            } catch (error) {
                failed.push({
                    tpl,
                    error: error?.message || String(error),
                });
            }
        }

        return { succeeded, failed };
    },

    requestTemplate: async (tplId) => ApiService.getData(`/v1/puzzle/wordwheel-tpl/${tplId}`),

    /** Admin daily preview. Does not require the Daily Bundle. */
    previewDaily: async (date) => {
        const param = date ? { date } : undefined;
        return ApiService.getData("/v1/puzzle/wordwheel-tpl/daily", param);
    },

    shuffleLayout: async (tplId) => AdminWordWheelBoardService.rearrangeWords(tplId),

    switchPuzzleLevel: async (tplId, puzzleLevel) => {
        const result = await AdminWordWheelBoardService.switchPuzzleLevel(tplId, puzzleLevel);
        seasonPublishedCache.clear();
        return result;
    },

    deleteTemplate: async (tplId) => {
        const result = await ApiService.deleteData(`/v1/puzzle/wordwheel-tpl/${tplId}`);
        return result;
    },

    deleteTemplates: async (tplIds) => {
        const result = await ApiService.deleteData("/v1/puzzle/wordwheel-tpl", { ids: tplIds });
        return result;
    },

    syncNamesFromCreatedAt: async (language) => {
        const lang = language && language !== "all" ? encodeURIComponent(language) : "english";
        return ApiService.putData(`/v1/puzzle/wordwheel-tpl/sync-names?language=${lang}`, {});
    },

    syncCharactersFromWords: async (language) => {
        const lang = language && language !== "all" ? encodeURIComponent(language) : "english";
        const result = await ApiService.putData(`/v1/puzzle/wordwheel-tpl/sync-characters?language=${lang}`, {});
        seasonPublishedCache.clear();
        return result;
    },

    removeSubsetDuplicates: async (language, dryRun = true) => {
        const lang = language && language !== "all" ? encodeURIComponent(language) : "english";
        const result = await ApiService.putData(
            `/v1/puzzle/wordwheel-tpl/remove-subset-duplicates?language=${lang}&dryRun=${dryRun}`,
            {}
        );
        if (!dryRun) {
            seasonPublishedCache.clear();
        }
        return result;
    },

    removeContainedWords: async (language, dryRun = true) => {
        const lang = language && language !== "all" ? encodeURIComponent(language) : "english";
        return ApiService.putData(
            `/v1/puzzle/wordwheel-tpl/remove-contained-words?language=${lang}&dryRun=${dryRun}`,
            {}
        );
    },

    removeBannedWords: async (language, dryRun = true) => {
        const lang = language && language !== "all" ? encodeURIComponent(language) : "english";
        return ApiService.putData(
            `/v1/puzzle/wordwheel-tpl/remove-banned-words?language=${lang}&dryRun=${dryRun}`,
            {}
        );
    },

    regenerateDisplayClues: async (language, dryRun = true, limit) => {
        const lang = language && language !== "all" ? encodeURIComponent(language) : "english";
        let url = `/v1/puzzle/wordwheel-tpl/regenerate-display-clues?language=${lang}&dryRun=${dryRun}`;
        if (limit != null && Number.isFinite(limit) && limit > 0) {
            url += `&limit=${encodeURIComponent(limit)}`;
        }
        return ApiService.putData(url, {});
    },

    fillDisplayCluesFromDictionary: async (tplId) =>
        ApiService.putData(`/v1/puzzle/wordwheel-tpl/${tplId}/fill-display-clues`, {}),

    fillAllMissingDisplayCluesFromDictionary: async (language, dryRun = true) => {
        const lang = language ? encodeURIComponent(language) : "all";
        return ApiService.putData(
            `/v1/puzzle/wordwheel-tpl/fill-missing-display-clues?language=${lang}&dryRun=${dryRun}`,
            {},
        );
    },

    lookupBannedHitsForTemplate: async (tpl) => {
        const lang = String(tpl?.language || "english").trim().toLowerCase() || "english";
        const list = await BannedService.requestList(lang);
        return findBannedHitsWithReasons(tpl, Array.isArray(list) ? list : []);
    },

    promoteTemplatesToReviewed: async (templates) => {
        const succeeded = [];
        const failed = [];
        const skipped = [];

        for (const tpl of templates) {
            try {
                if (tpl.state === REVIEWED || tpl.state === PUBLISHED) {
                    skipped.push(tpl);
                    continue;
                }
                await promoteOneToReviewed(tpl);
                succeeded.push(tpl);
            } catch (error) {
                failed.push({
                    tpl,
                    error: error?.message || String(error),
                });
            }
        }

        return { succeeded, failed, skipped };
    },

    promoteTemplatesToPublished: async (templates, publishOptions = null) => {
        const succeeded = [];
        const failed = [];
        const skipped = [];
        const toPublish = [];

        for (const tpl of templates) {
            if (tpl.state === PUBLISHED) {
                skipped.push(tpl);
                continue;
            }

            if (tpl.state === DRAFT || tpl.state === READY_TO_REVIEW) {
                try {
                    await promoteOneToReviewed(tpl, publishOptions);
                    toPublish.push({ ...tpl, state: REVIEWED });
                } catch (error) {
                    failed.push({
                        tpl,
                        error: error?.message || String(error),
                    });
                }
                continue;
            }

            if (tpl.state === REVIEWED) {
                toPublish.push(tpl);
                continue;
            }

            failed.push({
                tpl,
                error: `Cannot publish from state ${tpl.state}`,
            });
        }

        if (toPublish.length === 0) {
            return { succeeded, failed, skipped };
        }

        const publishItems = [];
        for (const tpl of toPublish) {
            try {
                publishItems.push(await buildPublishItemForTemplate(tpl, publishOptions));
            } catch (error) {
                failed.push({
                    tpl,
                    error: error?.message || String(error),
                });
            }
        }

        if (publishItems.length === 0) {
            return { succeeded, failed, skipped };
        }

        try {
            const result = await AdminWordWheelBoardService.publishTemplates(
                publishItems.map(({ tpl, ...item }) => item)
            );
            const failureById = new Map(
                (result?.failures || []).map((entry) => [entry.id, entry.message || "Failed to publish"])
            );

            publishItems.forEach((item) => {
                const failureMessage = failureById.get(item.id);
                if (failureMessage) {
                    failed.push({ tpl: item.tpl, error: failureMessage });
                } else if (result?.code === "FAILURE" && (result?.succeeded ?? 0) === 0) {
                    failed.push({
                        tpl: item.tpl,
                        error: result?.message || "Failed to publish",
                    });
                } else {
                    succeeded.push(item.tpl);
                }
            });
        } catch (error) {
            publishItems.forEach((item) => {
                failed.push({
                    tpl: item.tpl,
                    error: error?.message || String(error),
                });
            });
        }

        return { succeeded, failed, skipped };
    },
};

export default AdminWordWheelService;
